import { Check, MapPin, Phone, ShieldCheck, Sprout, Users, X, Zap } from "lucide-react";
import { Eyebrow, Page, PageHeader, Reveal } from "@/components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL, REGIONS } from "@/constants/site";

const COMPARISON = [
  { aspect: "Crew consistency", barnala: "Same trained crews, season after season", generic: "Rotating strangers every week" },
  { aspect: "Dispatch speed", barnala: "Local crews, often next-day", generic: "Days of back-and-forth" },
  { aspect: "Industry experience", barnala: "Farm, greenhouse & cannery specialists", generic: "Generalist temp workers" },
  { aspect: "Communication", barnala: `One direct line — ${PHONE_DISPLAY}`, generic: "Call-centre runaround" },
  { aspect: "Accountability", barnala: "Quality checks & on-site coordination", generic: "Drop-off and disappear" },
];

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Reliability",
    copy: "We treat every booking like a contract with the crop itself — because a missed morning can cost a harvest.",
  },
  {
    icon: Sprout,
    title: "Agricultural Know-How",
    copy: "Our crews know the difference between a ripe berry and a bruised one, and why it matters to your bottom line.",
  },
  {
    icon: Zap,
    title: "Speed",
    copy: "Local dispatch across the South Coast means we respond when the season — not the calendar — demands it.",
  },
  {
    icon: Users,
    title: "Fair Treatment",
    copy: "Workers who are paid fairly and treated with respect work harder and come back. Everyone wins.",
  },
];

export default function WhyUs() {
  return (
    <Page>
      <PageHeader
        eyebrow="Why Choose Us"
        title={
          <>
            The crew that <span className="italic text-[#86EFAC]">shows up.</span>
          </>
        }
        copy="Plenty of agencies promise workers. Barnala Labour Contracting delivers trained, punctual crews — and stands behind every shift."
      />

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>Barnala vs. Generic Agencies</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="comparison-table" className="mt-10 overflow-hidden rounded-2xl border border-white/10">
              <div className="grid grid-cols-3 bg-[#163B2C] px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#86EFAC]">
                <span>What matters</span>
                <span>Barnala</span>
                <span className="text-[#F6F4EE]/50">Generic agency</span>
              </div>
              {COMPARISON.map((row, i) => (
                <div
                  key={row.aspect}
                  data-testid={`comparison-row-${i}`}
                  className="grid grid-cols-3 items-start gap-4 border-t border-white/10 bg-[#0F291E] px-6 py-5 text-sm"
                >
                  <span className="font-semibold text-[#F6F4EE]">{row.aspect}</span>
                  <span className="flex items-start gap-2 text-[#F6F4EE]/85">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#22C55E]" />
                    {row.barnala}
                  </span>
                  <span className="flex items-start gap-2 text-[#F6F4EE]/45">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-[#F6F4EE]/30" />
                    {row.generic}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0B1F17] px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>Four Pillars</Eyebrow>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div
                  data-testid={`pillar-card-${i}`}
                  className="h-full rounded-2xl border border-white/10 bg-[#163B2C]/40 p-7 transition-colors duration-500 hover:border-[#22C55E]/50"
                >
                  <span className="font-mono text-xs font-semibold text-[#22C55E]">0{i + 1}</span>
                  <p.icon className="mt-4 h-6 w-6 text-[#22C55E]" />
                  <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/60">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>Coverage Breakdown</Eyebrow>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {REGIONS.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-white/10 bg-[#163B2C]/40 p-8">
                  <MapPin className="h-6 w-6 text-[#22C55E]" />
                  <h3 className="mt-5 font-display text-xl font-semibold tracking-tight sm:text-2xl">{r.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#F6F4EE]/60">{r.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <a
              href={PHONE_TEL}
              data-testid="whyus-call-button"
              className="mt-12 inline-flex items-center gap-2 rounded-full bg-[#22C55E] px-7 py-4 text-sm font-semibold text-[#0F291E] transition-transform duration-300 hover:scale-105"
            >
              <Phone className="h-4 w-4" />
              Talk to us — {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
